import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Pencil } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { Avatar } from '@components/common';
import { USER_ROLE_META, type User } from '../../types';

interface IdentityCardProps {
  user: User | null;
  isAnonScout: boolean;
  onEditProfile: () => void;
}

export function IdentityCard({ user, isAnonScout, onEditProfile }: IdentityCardProps) {
  const { colors } = useTheme();

  // Up to three role labels — enough to convey "who you are here" without
  // turning the header into a wall of tags. Fire Scout always leads.
  const roles = (user?.roles ?? []).slice(0, 3);

  return (
    <View style={styles.identity}>
      <Avatar
        imageUrl={isAnonScout ? null : user?.avatarUrl}
        name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'User'}
        anonymous={isAnonScout}
        size={56}
      />
      <View style={styles.body}>
        <Text style={[styles.name, { color: colors.text }]} numberOfLines={1}>
          {isAnonScout ? 'Fire Scout' : `${user?.firstName} ${user?.lastName}`}
        </Text>
        {!isAnonScout && user?.email ? (
          <Text style={[styles.email, { color: colors.textSecondary }]} numberOfLines={1}>
            {user.email}
          </Text>
        ) : null}

        {!isAnonScout && roles.length > 0 ? (
          <Text style={[styles.roleLine, { color: colors.textSecondary }]} numberOfLines={2}>
            {roles.map((role) => USER_ROLE_META[role]?.label ?? role).join(' · ')}
          </Text>
        ) : null}
      </View>

      {!isAnonScout && (
        <TouchableOpacity
          onPress={onEditProfile}
          style={styles.edit}
          accessibilityRole="button"
          accessibilityLabel="Edit profile"
          hitSlop={8}>
          <Pencil size={18} color={colors.textSecondary} />
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  identity: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACING.md,
    marginBottom: SPACING.lg,
  },
  body: {
    flex: 1,
    paddingTop: SPACING.xxs,
  },
  // The name IS this screen's title (the top bar is icons-only), so it takes
  // the light screenTitle register rather than a bold heading.
  name: {
    ...TYPOGRAPHY.screenTitle,
  },
  email: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  // Roles are metadata about who you are here, not a status to badge: one
  // small gray dot-separated line, the same register as the metadata blocks
  // on post detail and the structure sheet. textSecondary, not textTertiary —
  // these are content, and textTertiary is sub-AA by design (audit:a11y).
  roleLine: {
    ...TYPOGRAPHY.metadata,
    marginTop: SPACING.xs,
  },
  edit: {
    padding: SPACING.xs,
    marginTop: SPACING.xxs,
  },
});
